'use strict';

const { getHeight, getWidth } = require('../../frontend/get_dimensions');
const getOffset = require('../../frontend/get_offset');

function areObjectsEqual(a, b) {
  if (Object.keys(a).length !== Object.keys(b).length) {
    return false;
  }
  return Object.keys(a).every(aKey => b[aKey] && a[aKey] === b[aKey]);
}

class StickyHandler {
  constructor(
    element,
    container = document.body,
    elementOffset = 0,
    topElement
  ) {
    if (!element) {
      return;
    }

    this.resize = this.resize.bind(this);
    this.scroll = this.scroll.bind(this);

    this.element = element;
    this.container = container;
    this.elementOffset = elementOffset;
    this.topElement = topElement;

    this.shouldUpdateOffset = this.topElement != null;
    this.parent = this.element.parentNode;
    this.sibling = this.parent.nextElementSibling;
    this.firstTick = true;
    this.lastCss = {};
    this.scrollTop = window.scrollY;
    this.attachEvents();
    this.updatePosition();
    this.topElementHeight = this.topElement
      ? getHeight(this.topElement, { margin: false })
      : 0;

    this.parentPaddingLeft = parseInt(
      window.getComputedStyle(this.parent).paddingLeft
    );
  }

  updateDimensions() {
    this.windowHeight = window.document.documentElement.clientHeight;
    this.elementHeight = getHeight(this.element, {
      border: false,
      margin: false,
      padding: false,
    });
    this.elementWidth = getWidth(this.element, { margin: false });
    this.offsetLeft =
      getOffset(this.sibling).left +
      getWidth(this.sibling, { margin: false }) +
      this.parentPaddingLeft -
      window.scrollX;
    this.containerBottomPosition =
      getOffset(this.container).top +
      getHeight(this.container, {
        border: false,
        margin: false,
        padding: false,
      });
    this.scrollTop = window.scrollY;
    this.elementTop = getOffset(this.parent).top - this.elementOffset;
    this.elementBottomPosition =
      this.scrollTop + this.elementHeight + this.elementOffset;
    this.elementBottomPosition;
  }

  resize() {
    this.firstTick = true;
    this.updatePosition();
  }

  scroll() {
    this.firstTick = false;
    this.updatePosition();
  }

  updatePosition() {
    this.updateDimensions();
    this.css = this.getCss();
    if (!areObjectsEqual(this.css, this.lastCss)) {
      this.updateCss();
      this.lastCss = { ...this.css };
    }
  }

  updateCss() {
    Object.assign(this.element.style, this.css);
  }

  getCss() {
    if (
      this.isWindowSmallerThanElement() ||
      this.shouldBeRelative() ||
      this.isFixedPositionOverrideRequired()
    ) {
      return this.getRelativeCss();
    } else if (this.shouldBeAbsolute()) {
      return this.getAbsoluteCss();
    } else {
      return this.getFixedCss();
    }
  }

  getRelativeCss() {
    return {
      position: 'relative',
      top: `${
        this.isFixedPositionOverrideRequired() &&
        !this.isWindowSmallerThanElement()
          ? this.scrollTop - this.elementTop
          : 0
      }px`,
      width: '',
      left: '',
      transform: 'translate3d(0px,0px,0px)',
      '-webkit-overflow-scrolling': 'touch',
      overflow: 'hidden',
    };
  }

  getAbsoluteCss() {
    return {
      position: 'absolute',
      top: `${
        this.containerBottomPosition -
        this.elementHeight -
        this.elementTop -
        this.elementOffset -
        this.topElementHeight
      }px`,
      width: `${this.elementWidth}px`,
      left: `${this.parentPaddingLeft}px`,
      transform: 'translate3d(0px,0px,0px)',
      '-webkit-overflow-scrolling': 'touch',
      overflow: 'hidden',
    };
  }

  getFixedCss() {
    return {
      position: 'fixed',
      top: `${this.elementOffset}px`,
      width: `${this.elementWidth}px`,
      left: `${this.offsetLeft}px`,
      transform: 'translate3d(0px,0px,0px)',
      '-webkit-overflow-scrolling': 'touch',
      overflow: 'hidden',
    };
  }

  isFixedPositionOverrideRequired() {
    return (
      this.shouldBeFixed() &&
      (this.firstTick || this.windowHasHorizontalScroll())
    );
  }

  shouldBeRelative() {
    return this.scrollTop <= this.elementTop;
  }

  shouldBeAbsolute() {
    return (
      this.elementBottomPosition + this.topElementHeight >
      this.containerBottomPosition
    );
  }

  shouldBeFixed() {
    return !(this.shouldBeRelative() || this.shouldBeAbsolute());
  }

  isWindowSmallerThanElement() {
    return this.windowHeight < this.elementHeight;
  }

  windowHasHorizontalScroll() {
    return window.scrollX !== 0;
  }

  attachEvents() {
    window.addEventListener('scroll', this.scroll, false);
    window.addEventListener('resize', this.resize, false);
    document.body.addEventListener('modal-open', this.scroll);
  }
}

module.exports = StickyHandler;
